import React, { useEffect, useState } from 'react';
import './MyAccountAsideBar.css';
import AsideBarLink from '../AsidebarLink';
import AppInstallAsideBarLink from './AppInstallAsideBarLink';
import * as translator from '../../../../utils/translator';
import { clientConfig } from '../../../../config';
import { useCustomer } from '../../../../customHooks/useCustomer';
import { getAskADoctorPagePath } from '../../../../utils/helpers';
interface MyAccountAsideBarProps {
	activeMenuLinkId: string;
	asideBarData: {
		firstName: string;
		lastName: string;
		uuid: string;
		locale: {
			language_id: string;
			country_id: string;
		};
		orderPrescriptionDetailsPendingCustomerCount: number;
		orderConversationNotificationsCount: number;
		hasActiveCarePlusSubscription?: boolean;
		carePlusSubscriptionHashId?: string | null;
		carePlusHasActionRequired?: boolean;
		/** Suppresses the app-install placements for customers who already have the app. */
		isPairedToMobileApp?: boolean;
		/** Placement ids the api allows. Undefined keeps them all hidden. */
		appInstallPlacements?: string[];
	};
	// Mobile accordion open state is lifted to MyAccountLayout so the store
	// WalkMe can open the menu before highlighting its target item.
	showAllLinks: boolean;
	setShowAllLinks: React.Dispatch<React.SetStateAction<boolean>>;
	// Menu link id whose icon should be force-shown (desktop) while the store
	// WalkMe is active, regardless of which tab is selected. null when inactive.
	forceShowIconMenuLinkId?: string | null;
}

const MyAccountAsideBar: React.FC<MyAccountAsideBarProps> = ({ activeMenuLinkId, asideBarData, showAllLinks, setShowAllLinks, forceShowIconMenuLinkId = null }) => {
	const [show, setShow] = useState<boolean>(false); //state to selectively show <AsideBarLink>, passed as true by default (assumes component is available to all)
	const country = asideBarData.locale.country_id.toLowerCase();
	const language = asideBarData.locale.language_id;
	const countries = ['de', 'fr','ch','gb'];
	const { logout } = useCustomer();

	//Show side bar links based on country
	useEffect(() => {
		if (countries.includes(country.toLowerCase())) {
			setShow(true);
		}
	}, []);

	const iconByMenuLinkId: any = {
		'my-orders': { iconSrc: require('../../../../icons/Shopping-Bag-Secondary.svg').default, alt: 'shopping-bag' },
		'test-lab': { iconSrc: require('../../../../icons/Test-Lab-Secondary.svg').default, alt: 'test-lab' },
		'orders-subscription': { iconSrc: require('../../../../icons/Refresh-Secondary.svg').default, alt: 'refresh' },
		'questionnaire-defaults': { iconSrc: require('../../../../icons/File-Plus-Secondary.svg').default, alt: 'file-plus' },
		'delivery-address': {
			iconSrc: country === 'fr' ? require('../../../../icons/Map-Secondary.svg').default : require('../../../../icons/Delivery-Secondary.svg').default,
			alt: 'delivery',
		},
		'personal-data': { iconSrc: require('../../../../icons/User-Secondary.svg').default, alt: 'user' },
		'ask-a-doctor': { iconSrc: require('../../../../icons/Message-Secondary.svg').default, alt: 'message' },
		contact: { iconSrc: require('../../../../icons/Help-Secondary.svg').default, alt: 'help' },
		settings: { iconSrc: require('../../../../icons/side-bar-settings-secondary.svg').default, alt: 'settings' },
		'change-password': { iconSrc: require('../../../../icons/Lock-Secondary.svg').default, alt: 'lock' },
		logout: { iconSrc: require('../../../../icons/Log-Out-Secondary.svg').default, alt: 'logout' },
	};

	return (
		<aside className="account-asidebar-container">
			<div className="asidebar-details">
				<p className="asidebar-details-name">
					{translator.translate(language, 'my_account', 'AsideBar/name')} {asideBarData.firstName} {asideBarData.lastName}
				</p>
				<p className="asidebar-details-id">
					{translator.translate(language, 'my_account', 'AsideBar/uuid')} {asideBarData.uuid}
				</p>

				{/*Asidebar accordion - only shown in mobile size*/}
				<div className="asidebar-details-accordion" onClick={() => setShowAllLinks((current: boolean) => !current)}>
					<p className="asidebar-link">
						<img className="asidebar-link-icon" src={iconByMenuLinkId[activeMenuLinkId].iconSrc} alt={iconByMenuLinkId[activeMenuLinkId].alt} />
						{translator.translate(language, 'my_account', `AsideBar/${activeMenuLinkId}`)}
					</p>

					<img className={showAllLinks ? 'chevron-up' : 'chevron-down'} src={require('../../../../icons/Chevron-Down-Secondary.svg').default} alt="chevron" />
				</div>
				{/*End of Mobile Asidebar accordion */}
			</div>

			<ul className={showAllLinks ? 'asidebar-links' : 'asidebar-links-closed'}>
				<AsideBarLink
					id="my-orders"
					iconSrc={iconByMenuLinkId['my-orders'].iconSrc}
					linkText={translator.translate(language, 'my_account', 'AsideBar/my-orders')}
					link={`/${country}/account/my-orders`}
					alt={iconByMenuLinkId['my-orders'].alt}
					activeMenuLinkId={activeMenuLinkId}
					onClick={() => setShowAllLinks(false)}
					notificationsAmount={asideBarData.orderPrescriptionDetailsPendingCustomerCount}
				/>

				<AsideBarLink
					id="orders-subscription"
					iconSrc={iconByMenuLinkId['orders-subscription'].iconSrc}
					linkText={translator.translate(language, 'my_account', 'AsideBar/orders-subscription')}
					link={`/${country}/account/orders-subscription`}
					alt={iconByMenuLinkId['orders-subscription'].alt}
					activeMenuLinkId={activeMenuLinkId}
					onClick={() => setShowAllLinks(false)}
					showBadgeNew={!!asideBarData.carePlusHasActionRequired}
					badgeNewText={translator.translate(language, 'my_account', 'AsideBar/newBadge')}
					badgeNewColor="linear-gradient(90deg, #F59200 0%, #FFBB29 100%)"
					forceShowIcon={forceShowIconMenuLinkId === 'orders-subscription'}
					show={show}
				/>


				{country === 'de' && (
					<AsideBarLink
						id="test-lab"
						iconSrc={iconByMenuLinkId['test-lab'].iconSrc}
						linkText={translator.translate(language, 'my_account', 'AsideBar/test-lab')}
						link={`/${country}/account/test-lab`}
						alt={iconByMenuLinkId['test-lab'].alt}
						activeMenuLinkId={activeMenuLinkId}
						onClick={() => setShowAllLinks(false)}
						showBadgeNew={true}
						badgeNewText={translator.translate(language, 'my_account', 'AsideBar/newBadge')}
						badgeNewColor="linear-gradient(144deg, #1A9868 0%, #008935 100%)"
						forceShowIcon={forceShowIconMenuLinkId === 'test-lab'}
					/>
				)}

				{country !== 'fr' && (
					<AsideBarLink
						id="questionnaire-defaults"
						iconSrc={iconByMenuLinkId['questionnaire-defaults'].iconSrc}
						linkText={translator.translate(language, 'my_account', 'AsideBar/questionnaire-defaults')}
						link={`/${country}/account/questionnaire-defaults`}
						alt={iconByMenuLinkId['questionnaire-defaults'].alt}
						activeMenuLinkId={activeMenuLinkId}
						onClick={() => setShowAllLinks(false)}
						show={show}
					/>
				)}
				{/* Sits under the questionnaire rows to match the Figma sidebar */}
				<AppInstallAsideBarLink
					language={language}
					country={country}
					customerUuid={asideBarData?.uuid}
					isPairedToMobileApp={asideBarData?.isPairedToMobileApp}
					allowedPlacements={asideBarData?.appInstallPlacements}
					activeMenuLinkId={activeMenuLinkId}
					showAllLinks={showAllLinks}
				/>

				<AsideBarLink
					id="delivery-address"
					iconSrc={iconByMenuLinkId['delivery-address'].iconSrc}
					linkText={translator.translate(language, 'my_account', 'AsideBar/delivery-address')}
					link={`/${country}/account/delivery-address`}
					alt={iconByMenuLinkId['delivery-address'].alt}
					activeMenuLinkId={activeMenuLinkId}
					onClick={() => setShowAllLinks(false)}
				/>

				<AsideBarLink
					id="personal-data"
					iconSrc={iconByMenuLinkId['personal-data'].iconSrc}
					linkText={translator.translate(language, 'my_account', 'AsideBar/personal-data')}
					link={`/${country}/account/personal-data`}
					alt={iconByMenuLinkId['personal-data'].alt}
					activeMenuLinkId={activeMenuLinkId}
					onClick={() => setShowAllLinks(false)}
				/>

				<AsideBarLink
					id="ask-a-doctor"
					iconSrc={iconByMenuLinkId['ask-a-doctor'].iconSrc}
					linkText={translator.translate(language, 'my_account', 'AsideBar/ask-a-doctor')}
					link={`/${country}/account/${getAskADoctorPagePath(country)}`}
					alt={iconByMenuLinkId['ask-a-doctor'].alt}
					activeMenuLinkId={activeMenuLinkId}
					onClick={() => setShowAllLinks(false)}
					notificationsAmount={asideBarData.orderConversationNotificationsCount}
				/>

				<AsideBarLink
					id="contact"
					iconSrc={iconByMenuLinkId['contact'].iconSrc}
					linkText={translator.translate(language, 'my_account', 'AsideBar/contact')}
					link={`/${country}/contact`}
					alt={iconByMenuLinkId['contact'].alt}
					activeMenuLinkId={activeMenuLinkId}
					onClick={() => setShowAllLinks(false)}
				/>

				<AsideBarLink
					id="settings"
					iconSrc={iconByMenuLinkId['settings'].iconSrc}
					linkText={translator.translate(language, 'my_account', 'AsideBar/settings')}
					link={`/${country}/account/newsletter`}
					alt={iconByMenuLinkId['settings'].alt}
					activeMenuLinkId={activeMenuLinkId}
					onClick={() => setShowAllLinks(false)}
				/>

				<AsideBarLink
					id="change-password"
					iconSrc={iconByMenuLinkId['change-password'].iconSrc}
					linkText={translator.translate(language, 'my_account', 'AsideBar/change-password')}
					link={`/${country}/account/change-password`}
					alt={iconByMenuLinkId['change-password'].alt}
					activeMenuLinkId={activeMenuLinkId}
					onClick={() => setShowAllLinks(false)}
				/>

				<AsideBarLink
					id="logout"
					onClick={() => logout(clientConfig.MAIN_WEBSITE_URL + '/' + translator.getCMSUrlPrefixByLocale(language + '-' + country))}
					linkText={translator.translate(language, 'my_account', 'AsideBar/logout')}
					iconSrc={iconByMenuLinkId['logout'].iconSrc}
					alt={iconByMenuLinkId['logout'].alt}
					href={clientConfig.MAIN_WEBSITE_URL + '/' + translator.getCMSUrlPrefixByLocale(language + '-' + country)}
					activeMenuLinkId={activeMenuLinkId}
				/>
			</ul>
		</aside>
	);
};

export default MyAccountAsideBar;
